import { BehaviorSubject, Subject, from, Observable } from 'rxjs';
import { CoordinatorService } from './coordinator.service';

// MD5 function loaded from CDN (blueimp-md5)
declare const md5: (value: string) => string;

/**
 * Sync status for attachment records
 */
export type AttachmentSyncStatus = 'pending' | 'uploading' | 'synced' | 'failed';

/**
 * Attachment record stored in IndexedDB
 */
export interface AttachmentRecord {
    id: string;                    // MD5 hash of content (primary key)
    projectId: string;             // Project this belongs to (indexed)
    blob: Blob;                    // Actual file content
    md5Hash: string;               // MD5 hash (same as id, for verification)
    filename: string;              // Original filename
    mimetype: string;              // File MIME type
    size: number;                  // File size in bytes
    createdAt: number;             // Timestamp when stored locally
    syncStatus: AttachmentSyncStatus;
    lastSyncAttempt?: number;      // Timestamp of last upload attempt
    syncFailureCount: number;      // Number of failed sync attempts
    cloudPath?: string;            // Remote storage path once synced
}

/**
 * Result of storing an attachment
 */
export interface AttachmentStoreResult {
    success: boolean;
    record?: AttachmentRecord;
    error?: string;
    isExisting?: boolean;          // True if attachment already existed
}

const DB_NAME = 'referentka-attachments';
const DB_VERSION = 1;
const STORE_NAME = 'attachments';

/**
 * AttachmentStorage - Manages IndexedDB operations for attachment storage
 * 
 * Provides:
 * - Blob storage with MD5 hash calculation
 * - Project-scoped queries
 * - Sync status tracking
 * - Reactive API using RxJS
 */
export class AttachmentStorage {
    private _db: IDBDatabase | null = null;
    private _isReady$ = new BehaviorSubject<boolean>(false);
    private _error$ = new Subject<string>();

    // Observable for sync status changes
    private _syncStatusChange$ = new Subject<{ id: string; projectId: string; status: AttachmentSyncStatus }>();

    constructor(private coords: CoordinatorService) {
        this._initDatabase();
    }

    /**
     * Observable that emits true when the database is ready
     */
    get isReady$(): Observable<boolean> {
        return this._isReady$.asObservable();
    }

    /**
     * Observable that emits errors
     */
    get error$(): Observable<string> {
        return this._error$.asObservable();
    }

    /**
     * Observable that emits when sync status changes
     */
    get syncStatusChange$(): Observable<{ id: string; projectId: string; status: AttachmentSyncStatus }> {
        return this._syncStatusChange$.asObservable();
    }

    /**
     * Check if database is ready
     */
    get isReady(): boolean {
        return this._isReady$.getValue();
    }

    /**
     * Initialize the IndexedDB database
     */
    private _initDatabase(): void {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = (event) => {
            const error = 'Failed to open IndexedDB for attachments';
            console.error(error, event);
            this._error$.next(error);
        };

        request.onsuccess = (event) => {
            this._db = (event.target as IDBOpenDBRequest).result;
            console.log('%c AttachmentStorage IndexedDB ready', 'background: green; color: white;');
            this._isReady$.next(true);
        };

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            
            // Create the attachments store
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                
                // Create indexes for efficient querying
                store.createIndex('projectId', 'projectId', { unique: false });
                store.createIndex('syncStatus', 'syncStatus', { unique: false });
                store.createIndex('projectId_syncStatus', ['projectId', 'syncStatus'], { unique: false });
                
                console.log('%c AttachmentStorage IndexedDB schema created', 'background: blue; color: white;');
            }
        };
    }

    /**
     * Calculate MD5 hash from file content
     */
    async calculateMD5(file: File | Blob): Promise<string> {
        const buffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(buffer);
        // Convert to ISO-8859-1 string for MD5 calculation (same as worker does)
        const str = new TextDecoder('iso-8859-1').decode(uint8Array);
        return md5(str);
    }

    /**
     * Store an attachment in IndexedDB
     * 
     * @param file - The file to store
     * @param projectId - The project this attachment belongs to
     * @param filename - Original filename
     * @param mimetype - MIME type (optional, will be guessed from filename)
     * @param isCloud - Whether this is a cloud project (affects initial sync status)
     */
    async store(
        file: File | Blob,
        projectId: string,
        filename: string,
        mimetype?: string,
        isCloud: boolean = false
    ): Promise<AttachmentStoreResult> {
        if (!this._db) {
            return { success: false, error: 'Database not initialized' };
        }

        try {
            // Calculate MD5 hash
            const hash = await this.calculateMD5(file);
            
            // Check if already exists
            const existing = await this.get(hash, projectId);
            if (existing) {
                return { 
                    success: true, 
                    record: existing, 
                    isExisting: true 
                };
            }

            // Determine mimetype
            const finalMimetype = mimetype || 
                this.coords.utils$.guessMimetype(filename) || 
                'application/octet-stream';

            // Create the record
            const record: AttachmentRecord = {
                id: hash,
                projectId,
                blob: file instanceof File ? new Blob([file], { type: finalMimetype }) : file,
                md5Hash: hash,
                filename,
                mimetype: finalMimetype,
                size: file.size,
                createdAt: Date.now(),
                syncStatus: isCloud ? 'pending' : 'synced',
                syncFailureCount: 0,
            };

            // Store in IndexedDB
            await this._put(record);

            console.log('%c Attachment stored', 'color: green;', {
                id: hash,
                filename,
                size: this.coords.utils$.bytesToHumanReadable(file.size),
                projectId,
                isCloud
            });

            return { success: true, record };
        } catch (e: any) {
            const error = `Failed to store attachment: ${e.message}`;
            console.error(error, e);
            this._error$.next(error);
            return { success: false, error };
        }
    }

    /**
     * Get an attachment by ID and projectId
     */
    async get(id: string, projectId: string): Promise<AttachmentRecord | null> {
        if (!this._db) return null;

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(id);

            request.onsuccess = () => {
                const record = request.result as AttachmentRecord | undefined;
                // Verify project ownership
                if (record && record.projectId === projectId) {
                    resolve(record);
                } else {
                    resolve(null);
                }
            };

            request.onerror = () => {
                reject(new Error('Failed to get attachment'));
            };
        });
    }

    /**
     * Get an attachment by ID only (for cross-project lookup during migration)
     */
    async getById(id: string): Promise<AttachmentRecord | null> {
        if (!this._db) return null;

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(id);

            request.onsuccess = () => {
                resolve(request.result || null);
            };

            request.onerror = () => {
                reject(new Error('Failed to get attachment'));
            };
        });
    }

    /**
     * Get all attachments for a project
     */
    async getByProject(projectId: string): Promise<AttachmentRecord[]> {
        if (!this._db) return [];

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const index = store.index('projectId');
            const request = index.getAll(projectId);

            request.onsuccess = () => {
                resolve(request.result || []);
            };

            request.onerror = () => {
                reject(new Error('Failed to get attachments by project'));
            };
        });
    }

    /**
     * Get all attachments with a specific sync status for a project
     */
    async getByProjectAndStatus(
        projectId: string, 
        syncStatus: AttachmentSyncStatus
    ): Promise<AttachmentRecord[]> {
        if (!this._db) return [];

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const index = store.index('projectId_syncStatus');
            const request = index.getAll([projectId, syncStatus]);

            request.onsuccess = () => {
                resolve(request.result || []);
            };

            request.onerror = () => {
                reject(new Error('Failed to get attachments by project and status'));
            };
        });
    }

    /**
     * Update sync status of an attachment
     */
    async updateSyncStatus(
        id: string,
        projectId: string,
        status: AttachmentSyncStatus,
        cloudPath?: string
    ): Promise<boolean> {
        const record = await this.get(id, projectId);
        if (!record) return false;

        record.syncStatus = status;
        record.lastSyncAttempt = Date.now();
        
        if (status === 'failed') {
            record.syncFailureCount++;
        } else if (status === 'synced') {
            record.syncFailureCount = 0;
            if (cloudPath) {
                record.cloudPath = cloudPath;
            }
        }

        await this._put(record);
        
        // Emit sync status change
        this._syncStatusChange$.next({ id, projectId, status });

        return true;
    }

    /**
     * Delete an attachment
     */
    async delete(id: string, projectId: string): Promise<boolean> {
        if (!this._db) return false;

        // Verify ownership first
        const record = await this.get(id, projectId);
        if (!record) return false;

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.delete(id);

            request.onsuccess = () => {
                console.log('%c Attachment deleted', 'color: orange;', { id, projectId });
                resolve(true);
            };

            request.onerror = () => {
                reject(new Error('Failed to delete attachment'));
            };
        });
    }

    /**
     * Delete all attachments for a project
     */
    async deleteByProject(projectId: string): Promise<number> {
        const attachments = await this.getByProject(projectId);
        let deleted = 0;

        for (const att of attachments) {
            if (await this.delete(att.id, projectId)) {
                deleted++;
            }
        }

        console.log('%c Project attachments deleted', 'color: orange;', { projectId, count: deleted });
        return deleted;
    }

    /**
     * Get blob content for an attachment
     */
    async getBlob(id: string, projectId: string): Promise<Blob | null> {
        const record = await this.get(id, projectId);
        return record?.blob || null;
    }

    /**
     * Create an object URL for an attachment (caller must revoke when done)
     */
    async createObjectURL(id: string, projectId: string): Promise<string | null> {
        const record = await this.get(id, projectId);
        if (!record) return null;
        
        return URL.createObjectURL(
            new Blob([record.blob], { type: record.mimetype })
        );
    }

    /**
     * Store a record in IndexedDB (internal)
     */
    private _put(record: AttachmentRecord): Promise<void> {
        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.put(record);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(new Error('Failed to store attachment record'));
        });
    }

    /**
     * Store a blob that was downloaded from cloud (for caching)
     */
    async storeFromCloud(
        blob: Blob,
        id: string,
        projectId: string,
        filename: string,
        mimetype: string,
        cloudPath: string
    ): Promise<AttachmentStoreResult> {
        if (!this._db) {
            return { success: false, error: 'Database not initialized' };
        }

        try {
            // Check if already exists
            const existing = await this.get(id, projectId);
            if (existing) {
                return { success: true, record: existing, isExisting: true };
            }

            const record: AttachmentRecord = {
                id,
                projectId,
                blob,
                md5Hash: id,
                filename,
                mimetype,
                size: blob.size,
                createdAt: Date.now(),
                syncStatus: 'synced',
                syncFailureCount: 0,
                cloudPath,
            };

            await this._put(record);

            console.log('%c Attachment cached from cloud', 'color: blue;', {
                id,
                filename,
                projectId
            });

            return { success: true, record };
        } catch (e: any) {
            const error = `Failed to cache cloud attachment: ${e.message}`;
            console.error(error, e);
            return { success: false, error };
        }
    }

    /**
     * Get count of attachments by status for a project
     */
    async getStatusCounts(projectId: string): Promise<Map<AttachmentSyncStatus, number>> {
        const counts = new Map<AttachmentSyncStatus, number>();
        const attachments = await this.getByProject(projectId);
        
        for (const att of attachments) {
            const current = counts.get(att.syncStatus) || 0;
            counts.set(att.syncStatus, current + 1);
        }
        
        return counts;
    }

    /**
     * Check if all attachments for a project are synced
     */
    async isProjectFullySynced(projectId: string): Promise<boolean> {
        const pending = await this.getByProjectAndStatus(projectId, 'pending');
        const uploading = await this.getByProjectAndStatus(projectId, 'uploading');
        const failed = await this.getByProjectAndStatus(projectId, 'failed');
        
        return pending.length === 0 && uploading.length === 0 && failed.length === 0;
    }
}

